import React, { useMemo } from 'react';
import styled from 'styled-components';
import moment from 'moment';
import { cleanTeacherName } from '../../Share/Normal/useCoachName.js';

// 상수: 요일 배열
const DAYS_OF_WEEK = ['Sun', 'Mon', 'Tue', 'Wed', 'Thu', 'Fri', 'Sat'];

export default function CoachFilterList(props) {
  const { scheduleData, date, teachers, lessonsWithTeachers, changedTeachers, selectedCoaches, setSelectedCoaches } = props;

  // lessonId로 빠르게 조회하기 위한 Map 생성
  const lessonsMap = useMemo(() => {
    if (!lessonsWithTeachers || lessonsWithTeachers.length === 0) return new Map();

    const map = new Map();

    lessonsWithTeachers.forEach(lesson => {
      if (lesson.id && lesson.teachers) {
        map.set(lesson.id, lesson.teachers); // teachers 배열 저장
      }
    });

    return map;
  }, [lessonsWithTeachers]);

  // 현재 월의 코치별 수업 수 계산
  const teacherClassCount = useMemo(() => {
    if (!teachers || teachers.length === 0) return [];

    if (!scheduleData || scheduleData.length === 0) return [];

    // teachers 배열의 코치들을 0으로 초기화
    const countMap = {};

    teachers.forEach(teacher => {
      if (teacher.teacherName) {
        countMap[teacher.teacherName] = 0;
      }
    });

    countSchedulesByTeacher(scheduleData, date, countMap, lessonsMap);

    const result = [];

    teachers.forEach(teacher => {
      if (teacher.teacherName) {
        result.push({
          name: teacher.teacherName,
          count: countMap[teacher.teacherName] || 0
        });
      }
    });

    return result;
  }, [scheduleData, date, teachers, lessonsMap]);

  // 주차 계산
  function getWeekNumber(dateString) {
    const standard = new Date(dateString);
    const currentDate = standard.getDate();
    const startOfMonth = new Date(standard.setDate(1));
    const weekDay = startOfMonth.getDay();

    return (parseInt(((weekDay - 1) + currentDate) / 7) + 1).toString();
  }

  // lessonId로 모든 코치 이름 가져오기
  function getTeacherNames(schedule, lessonsMap) {
    if (schedule.lessonId && lessonsMap.has(schedule.lessonId)) {
      const teachers = lessonsMap.get(schedule.lessonId);
      
      if (teachers && teachers.length > 0) {
        return teachers;
      }
    }

    return schedule.teacherNames || (schedule.teacherName ? [schedule.teacherName] : []);
  }

  function createChangedTeachersMap(changedTeachers) {
    const map = new Map();
    if (!changedTeachers || changedTeachers.length === 0) {
      return map;
    }

    changedTeachers.forEach(changed => {
      const key = `${changed.scheduleId}_${moment(changed.classDate).format('YYYY.MM.DD')}`;

      let teacherNames = [];
      if (changed.teacherNames) {
        if (typeof changed.teacherNames === 'string') {
          teacherNames = changed.teacherNames.split(',').map(cleanTeacherName);
        }
        else if (Array.isArray(changed.teacherNames)) {
          teacherNames = changed.teacherNames.map(cleanTeacherName);
        }
      }

      map.set(key, teacherNames);
    });

    return map;
  }

  function countSchedulesByTeacher(scheduleData, date, countMap, lessonsMap) {
    const firstWeek = date.clone().startOf('month').week();
    const lastWeek = date.clone().endOf('month').week() === 1 ? 53 : date.clone().endOf('month').week();

    const startDate = date.clone().startOf('year').week(firstWeek).startOf('week');
    const endDate = date.clone().startOf('year').week(lastWeek).endOf('week');

    const changedTeachersMap = createChangedTeachersMap(changedTeachers);

    scheduleData.forEach((schedule) => {
      const scheduleStartDate = moment(schedule.classDate.split('T')[0]);
      const scheduleEndDate = moment(schedule.endDate);

      const effectiveStartDate = moment.max(scheduleStartDate, startDate);
      const effectiveEndDate = moment.min(scheduleEndDate, endDate);

      const defaultTeacherNames = getTeacherNames(schedule, lessonsMap).map(cleanTeacherName);

      if (defaultTeacherNames.length === 0) {
        return;
      }

      const targetDays = schedule.applyWeek === 'All' ? DAYS_OF_WEEK : schedule.applyWeek.split(',');
      const repeatWeekSet = new Set(schedule.repeatWeek);

      targetDays.forEach(dayOfWeek => {
        let currentDate = startDate.clone();
        const targetDayIndex = DAYS_OF_WEEK.indexOf(dayOfWeek);

        if (targetDayIndex === -1) {
          return;
        }

        while (currentDate.day() !== targetDayIndex) {
          currentDate.add(1, 'day');
        }

        while (currentDate.isSameOrBefore(endDate)) {
          if (currentDate.isSameOrAfter(effectiveStartDate) && currentDate.isBefore(effectiveEndDate)) {
            const weekNumber = getWeekNumber(currentDate.format('YYYY-MM-DD'));

            const isValidWeek = repeatWeekSet.has(weekNumber) || repeatWeekSet.has('0');

            if (isValidWeek) {
              const dateKey = `${schedule.scheduleId}_${currentDate.format('YYYY.MM.DD')}`;
              const changedTeacherNames = changedTeachersMap.get(dateKey);

              const finalTeacherNames = changedTeacherNames || defaultTeacherNames;

              finalTeacherNames.forEach(name => {
                if (name) {
                  countMap[name] = (countMap[name] || 0) + 1;
                }
              });
            }
          }
          currentDate.add(7, 'days');
        }
      });
    });
  }

  const currentTeachers = selectedCoaches || [];

  const selectedTeachersSet = useMemo(() => {
    return new Set(currentTeachers);
  }, [currentTeachers]);

  // 전체 확인 버튼 클릭 핸들러
  const handleAllClick = () => {
    setSelectedCoaches([]);
  };

  // 코치 버튼 클릭 핸들러
  const handleTeacherClick = (teacherName) => {
    if (selectedTeachersSet.has(teacherName)) {
      // 이미 선택되어 있으면 제거
      setSelectedCoaches(currentTeachers.filter(name => name !== teacherName));
    }
    else {
      // 선택되어 있지 않으면 추가
      setSelectedCoaches([...currentTeachers, teacherName]);
    }
  };

  return (
    <Wrapper>
      <AllTeacherButtonWrapper>
        <TeacherButton
          isSelected={currentTeachers.length === 0}
          onClick={handleAllClick}>
          <TeacherText isSelected={currentTeachers.length === 0}>전체 확인</TeacherText>
        </TeacherButton>
      </AllTeacherButtonWrapper>
      <TeacherButtonWrapper>
        {teacherClassCount.map((teacher) => {
          const isSelected = selectedTeachersSet.has(teacher.name);

          return (
            <TeacherButton
              key={teacher.name}
              isSelected={isSelected}
              onClick={() => handleTeacherClick(teacher.name)}>
              <TeacherText isSelected={isSelected}>{teacher.name}: {teacher.count}</TeacherText>
              <CountText isSelected={isSelected}>회</CountText>
            </TeacherButton>
          );
        })}
      </TeacherButtonWrapper>
    </Wrapper>
  );
}

const Wrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  align-items: flex-start;
  gap: 10px;

  width: 1020px;

  margin-bottom: 16px;
`;

const AllTeacherButtonWrapper = styled.div`
  display: flex;
`;

const TeacherButtonWrapper = styled.div`
  display: flex;
  flex-direction: row;
  flex-wrap: wrap;

  flex: 1;
  min-width: 0;

  gap: 10px;
`;

const TeacherButton = styled.div`
  display: flex;
  flex-direction: row;

  justify-content: center;
  align-items: flex-end;

  padding: 8px 20px;

  border-radius: 8px;
  background-color: ${props => props.isSelected ? '#6DD49E' : ''};

  transition: all 0.2s ease-in-out;

  @media(hover: hover) {
    &:hover {
      cursor: pointer;
      background-color: ${props => props.isSelected ? '#2EAA69' : '#ECECEC'};
    }
  }
`;

const TeacherText = styled.span`
  font-family: NotoSansKR-Bold;
  font-size: 14px;
  color: ${props => props.isSelected ? '#FFFFFF' : '#969696'};
`;

const CountText = styled.span`
  font-family: NotoSansKR-Bold;
  font-size: 10px;
  color: ${props => props.isSelected ? '#FFFFFF' : '#969696'};
  margin-bottom: 2px;
`;